/**
 * `import()` de um pedaço de JS que não é essencial à página, com novas tentativas.
 *
 * Quando o download de um pedaço falha (conexão que caiu, pedido bloqueado por extensão, rede lenta que estoura o tempo),
 * o webpack rejeita o `import()` com ChunkLoadError e esquece o pedaço: o `import()` seguinte baixa de novo. Aqui cada
 * falha espera um pouco e tenta outra vez, até acabarem as esperas de `delays`. Se o chamador desistir no meio
 * (`isCancelled`, ex.: o componente saiu da página), não há nova tentativa e a promessa rejeita com o último erro.
 * Quem chama decide o que mostrar sem o pedaço e trata a rejeição (sem `.catch`, ela vira erro não tratado no navegador).
 */
export const IMPORT_RETRY_DELAYS_MS: readonly number[] = [1000, 4000];

interface ImportWithRetryOptions {
  delays?: readonly number[];
  isCancelled?: () => boolean;
}

const wait = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

export async function importWithRetry<T>(load: () => Promise<T>, { delays = IMPORT_RETRY_DELAYS_MS, isCancelled = () => false }: ImportWithRetryOptions = {}): Promise<T> {
  let lastError: unknown;
  for (let attempt = 0; attempt <= delays.length; attempt += 1) {
    if (attempt > 0) {
      // eslint-disable-next-line no-await-in-loop -- uma tentativa por vez, depois da espera da anterior
      await wait(delays[attempt - 1] as number);
      if (isCancelled()) break;
    }
    try {
      // eslint-disable-next-line no-await-in-loop -- idem
      return await load();
    } catch (error) {
      lastError = error;
      if (isCancelled()) break;
    }
  }
  throw lastError;
}
